// MATERIAL UI
import {
  AccordionSummary,
  Button,
  Pagination,
  Stack,
  styled,
  useTheme,
  Box,
  Divider,
} from "@mui/material";

// REACT
import { useState } from "react";

// COMPONENTS
import {
  DropDownRedArrow,
  StyledAccordion,
  StyledAccordionDetails,
  StyledLabel,
  StyledTab,
  StyledTitle,
} from "../styles/TableStyledComponents";
import CancelModal from "./CancelModal";
import OrderDetails from "./OrderDetails";
import YourPlan from "./YourPlan";
import { LoadingSpinner } from "src/components/application/LoadingSpinner";
import { NoOrders } from "../../components/NoOrders";

// REDUX
import api from "src/store/features/api";
import { DcaStrategyDto, GetDcaStrategiesApiArg } from "src/store/features/api/generated";
import { useAppDispatch } from "src/store/store";

// UTIL FUNCTIONS
import { dateFormatter } from "src/utils/helpers/DateAndTimeHelpers";
import { setSelectedStrategy } from "src/store/features/dca.slice";
import { dynamicUnitFormatter, formatCurrency } from "src/utils/helpers/PriceAndUnitsHelpers";

// ICONS
import CachedIcon from "@mui/icons-material/Cached";

// AMPLITUDE
import { trackEvent } from "src/Amplitude";
import ContainedButton from "src/components/common/ContainedButton";
import { ClearIconWrapper } from "../../styles/Trade.styles";

const RecentTradesWrapper = styled("div")(({ theme }) => ({
  width: "100%",
  height: "100%",
  background: "#FFFFFF",
  borderRadius: "20px",
  boxShadow: "0px 4px 20px 0px rgba(173, 192, 203, 0.30)",
}));

export const Wrapper = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  height: "100%",
  width: "100%",
  padding: "24px",
  gap: "12px",
  borderRadius: "20px",
}));

interface IHeaderButtonProps {
  label: string;
  value: string;
  currentPage: string;
  onClick: (value: string) => void;
}

export const HeaderButton = styled(Button)<IHeaderButtonProps>(({ value, currentPage }) => ({
  color: value === currentPage ? "#002035" : "#5C6469",
  background: value === currentPage ? "#F8F9F9" : "#FFFFFF",
  fontFamily: "Roobert",
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "16px",
  alignItems: "center",
  textTransform: "none",
  padding: "8px",
  gap: "8px",
}));

export const LoadingWrapper = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  width: "100%",
  height: "100%",
}));

export const ContentWrapper = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  flex: "1 0 0",
  alignSelf: "stretch",
}));

const ActiveTabDot = styled("span")(({ theme }) => ({
  width: "8px",
  height: "8px",
  borderRadius: "50%",
  backgroundColor: "#0BEC9F",
  marginLeft: "6px",
}));

export default function StrategiesTable() {
  const [openCancelModal, setOpenCancelModal] = useState(false);
  const [modalPage, setModalPage] = useState<string>("inProgress");

  const pendingStatuses = ["Active"];
  const completedStatuses = ["Completed", "Cancelled"];

  const [paginationParams, setPaginationParams] = useState<GetDcaStrategiesApiArg>({
    page: 1,
    filters: pendingStatuses,
  });

  const handlePagination = (event: unknown, newPage: number): void => {
    setPaginationParams((prev) => ({
      ...prev,
      page: newPage,
    }));
  };

  const theme = useTheme();
  const {
    data: { strategies, pageCount, page } = {},
    isFetching,
    refetch: refetchStrategies,
  } = api.useGetDcaStrategiesQuery(paginationParams);

  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);

  const handleRefetch = async () => {
    setIsRefreshing(true);

    try {
      await refetchStrategies();
    } finally {
      setIsRefreshing(false);
    }
  };

  const handlePageChange = (modalPage: "inProgress" | "orderHistory") => {
    setPaginationParams((prev) => ({
      page: 1,
      filters: modalPage === "inProgress" ? pendingStatuses : completedStatuses,
    }));
    setModalPage(modalPage);
  };

  const TableRow = ({ strategy, iterator }: { strategy: DcaStrategyDto; iterator: number }) => {
    const [tabValue, setTabValue] = useState(0);

    const theme = useTheme();
    const dispatch = useAppDispatch();

    let tabs = ["Your Plan", "Orders"];

    const handleChange = (newValue: number) => {
      setTabValue(newValue);
      trackEvent("Switched tab", { tab: tabs[newValue] }, "Scheduled_Orders");
    };

    const {
      amount,
      buyCurrency: { code: fiatToken, logoUrl },
      sellCurrency: { code: sellToken },
      frequency,
      startDate,
      pastTriggers,
      totalTriggers,
      status,
      accumulatedBalance,
    } = strategy;

    if (status === "Cancelled" && !pastTriggers) tabs = ["Your Plan"];

    const CustomTabs = () => {
      return (
        <Stack direction="row" mb={2} ml={0.5} gap={2}>
          {tabs.map((tab, index) => (
            <StyledTab selected={tabValue === index} onClick={() => handleChange(index)}>
              {tab}
            </StyledTab>
          ))}
        </Stack>
      );
    };

    return (
      <StyledAccordion elevation={0} disableGutters={true}>
        <AccordionSummary
          sx={{
            padding: "16px 12px",
            margin: 0,
            "&:hover": { background: theme.palette.primary.contrastText, borderRadius: "10px" },
          }}
          expandIcon={
            <div style={{ margin: "0px 10px" }}>
              <DropDownRedArrow />
            </div>
          }>
          <Stack justifyContent={"space-between"} flex="auto" gap={0.5}>
            <div style={{ display: "flex", gap: "8px" }}>
              <img alt="crypto logo" src={logoUrl} style={{ width: "24px", height: "24px" }} />
              <StyledTitle>{`${formatCurrency(
                amount,
                sellToken,
              )} ${fiatToken} ${frequency}`}</StyledTitle>
            </div>
            <StyledLabel>{dateFormatter(startDate)}</StyledLabel>
          </Stack>
          <Stack textAlign="right" gap={0.5}>
            <StyledTitle>
              {status === "Cancelled" ? "Cancelled  " : `${pastTriggers}/${totalTriggers ?? "-"}  `}
            </StyledTitle>
            <StyledLabel>{`${dynamicUnitFormatter(
              accumulatedBalance ?? 0,
              0,
            )} ${fiatToken}`}</StyledLabel>
          </Stack>
        </AccordionSummary>

        <StyledAccordionDetails>
          {/* Tabs */}
          <CustomTabs />
          {/* Your Plan Tab */}
          {tabValue === 0 && <YourPlan strategy={strategy} />}
          {/* Order Details Tab */}
          {tabValue === 1 && <OrderDetails strategy={strategy} />}
          {/* Cancel Button */}
          {status === "Active" && (
            <Button
              disableRipple
              variant="outlined"
              fullWidth
              sx={{
                borderRadius: "8px",
                textTransform: "capitalize",
                backgroundColor: theme.palette.secondary.main,
                color: theme.palette.mode === "light" ? "#fff" : theme.palette.text.primary,
                borderColor: theme.palette.secondary.main,
              }}
              onClick={() => {
                dispatch(setSelectedStrategy(strategy));
                setOpenCancelModal(true);
                trackEvent("Open Cancel Modal", { id: strategy._id }, "Scheduled_Orders");
              }}>
              Cancel
            </Button>
          )}
        </StyledAccordionDetails>
      </StyledAccordion>
    );
  };

  return (
    <RecentTradesWrapper>
      <Wrapper>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
            alignItems: "center",
          }}>
          <Box>
            <HeaderButton
              label="In Progress"
              value="inProgress"
              currentPage={modalPage}
              onClick={() => handlePageChange("inProgress")}
              role="tab"
              aria-selected={modalPage === "inProgress"}>
              In Progress
              <ActiveTabDot />
            </HeaderButton>
            <HeaderButton
              label="Order History"
              value="orderHistory"
              currentPage={modalPage}
              onClick={() => handlePageChange("orderHistory")}
              role="tab"
              aria-selected={modalPage === "orderHistory"}>
              Order History
            </HeaderButton>
          </Box>
          <ContainedButton
            id="refresh-button"
            color="neutral"
            borderRadius="20px"
            sx={{
              boxShadow: "none",
              border: "1px solid #E5E9EB",
              color: "#002035",
              width: "auto",
              fontWeight: 500,
              maxHeight: "32px",
              padding: "4px 8px",
              cursor: "pointer",
              gap: "0px",
              pointerEvents: "auto",
              transition: "all 0.2s",
              "&:hover": {
                color: theme.palette.mode === "light" ? "#002035" : "#FFFFFF",
                backgroundColor: "#F8F9F9",
              },
              "&:active": {
                backgroundColor: "#E5E9EB",
              },
              "&:disabled": {
                backgroundColor: "#F8F9F9",
                color: "#A0A4A8",
              },
            }}
            endIcon={
              <ClearIconWrapper isLoading={isRefreshing} data-testid="refresh-button">
                <CachedIcon fontSize="small" sx={{ transform: "scaleX(-1)" }} />
              </ClearIconWrapper>
            }
            disabled={isFetching}
            onClick={() => handleRefetch()}
            data-testid="refresh-button"
            aria-label="Refresh data">
            <></>
          </ContainedButton>
        </Box>
        <Divider sx={{ color: "#E5E7E8", width: "100%", marginBottom: "16px" }} />
        {isFetching ? (
          <LoadingWrapper>
            <LoadingSpinner size={40} />
          </LoadingWrapper>
        ) : (
          <ContentWrapper>
            {strategies?.length ? (
              strategies?.map((strategy, it: number) => {
                return <TableRow key={strategy._id} strategy={strategy} iterator={it} />;
              })
            ) : (
              <NoOrders modalPage={modalPage} tradeType="scheduled" />
            )}
          </ContentWrapper>
        )}
        {!isFetching && strategies && strategies?.length > 0 && (
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
              paddingTop: "27px",
              bottom: "20px",
            }}>
            <Pagination
              sx={{ display: "flex", justifyContent: "center" }}
              onChange={handlePagination}
              count={pageCount}
              page={page}
              showFirstButton
              showLastButton
            />
          </Box>
        )}
        <CancelModal open={openCancelModal} setOpen={setOpenCancelModal} />
      </Wrapper>
    </RecentTradesWrapper>
  );
}
